import { BATCH_ANDROID_API_KEY, BATCH_IOS_API_KEY, PUSH_NOTIFICATION_ACCEPTATION_STORAGE_KEY, CORE_EVENTS } from "@core/constants";
import { EventManager } from "@core/managers/event.manager";
import { Logger } from "@core/helpers/logger.helpers";
import { StorageManager } from "@core/managers/storage.manager";
import { isIOS } from "@core/plugins/mobile/capacitor";
import { Device, isNative } from "@core/plugins/mobile";
import { useBatchStore } from "@core/modules/Batch";

let _started = false;

export class BatchManager {
  static get batch() {
    return window.batch;
  }

  static get started() {
    return _started;
  }

  static async hasUserDecided() {
    return (await BatchManager.getUserDecision()) !== null;
  }

  static async hasUserAccepted() {
    return (await BatchManager.getUserDecision()) === true;
  }

  static async setUserDecision(value) {
    return StorageManager.setItem(PUSH_NOTIFICATION_ACCEPTATION_STORAGE_KEY, value);
  }

  static async getUserDecision() {
    return StorageManager.getItem(PUSH_NOTIFICATION_ACCEPTATION_STORAGE_KEY) || null;
  }

  static async deleteUserDecision() {
    return StorageManager.removeItem(PUSH_NOTIFICATION_ACCEPTATION_STORAGE_KEY);
  }

  static batchPluginInstalled() {
    return BatchManager.batch !== undefined;
  }

  static batchStarted() {
    return BatchManager.started === true;
  }

  static initialize({ router }) {
    Logger.info("## BatchManager.initialize ### start initialization");
    if (!BatchManager.batchPluginInstalled()) {
      Logger.info("## BatchManager.initialize ### batch is not installed");
      return;
    }
    Logger.info("## BatchManager.initialize ### batch plugin is installed");
    // https://doc.batch.com/cordova/legacy/sdk-integration/#setting-up-the-sdk
    BatchManager.batch.setConfig({ androidAPIKey: BATCH_ANDROID_API_KEY, iOSAPIKey: BATCH_IOS_API_KEY });
    BatchManager.registerEvents({ router });
    // we start the plugin asap but we will start the link between api and device later (after auth)
    BatchManager.batch.start();
    BatchManager.batch.messaging.setDoNotDisturbEnabled(true);
    Logger.info("## BatchManager.messaging ### we set setDoNotDisturbEnabled at true");
    if (isIOS) {
      // https://doc.batch.com/cordova/sdk-changelog/#5_0_0
      Logger.info("## BatchManager.initialize ### ios refresh Token");
      BatchManager.batch.push.refreshToken();
    }
    Logger.info("## BatchManager.initialize ### batch.setConfig initialized");
  }

  static async start({ storeAction, storeGetter, env }) {
    const batchStore = useBatchStore();
    Logger.info("## BatchManager.start ###");
    if (!BatchManager.batchPluginInstalled()) {
      Logger.info("## BatchManager.start ### batch is not installed");
      return;
    }
    if (BatchManager.batchStarted()) {
      Logger.info("## BatchManager.start ### batch is already started");
      return;
    }
    try {
      const hasUserDecided = await BatchManager.hasUserDecided();
      Logger.info("## BatchManager.start ### has user decided", hasUserDecided);
      if (!hasUserDecided) {
        Logger.info("## BatchManager.start ### user has not decided yet");
        await BatchManager.handleUserDecision();
      }
      const installationId = await BatchManager.batch.user.getInstallationID();
      const operatingSystem = await Device.getOperatingSystem();
      const osVersion = await Device.getOperatingSystemVersion();
      Logger.info("## BatchManager.start ### user operatingSystem", operatingSystem);
      Logger.info("## BatchManager.start ### user osVersion", osVersion);
      Logger.info("## BatchManager.start ### user installationId", installationId);
      await batchStore[storeAction]({ operatingSystem, installationId, osVersion, env });
      const { data } = batchStore[storeGetter];
      const customId = data.custom_id;
      Logger.info("## BatchManager.start ### user customId", customId);
      BatchManager.activate({ customId });
      Logger.info("## BatchManager.start ### batch successfully started");
    } catch (error) {
      Logger.error("## BatchManager.start ### batch either encountered an error or user refused notifications", error);
    }
  }

  static async handleUserDecision() {
    try {
      Logger.info("## BatchManager.handleUserDecision ###");
      BatchManager.batch.push.requestNotificationAuthorization();
      BatchManager.setUserDecision(true);
      Logger.info("## BatchManager.handleUserDecision ### user (or system) decided to accept notification");
      BatchManager.batch.messaging.setDoNotDisturbEnabled(false);
      Logger.info("## BatchManager.messaging ### we set setDoNotDisturbEnabled at false");
    } catch (error) {
      BatchManager.setUserDecision(false);
      Logger.info("## BatchManager.handleUserDecision ### user decided not to accept notification");
      throw error;
    }
  }

  static async resetBatch() {
    BatchManager.deactivate();
    BatchManager.deleteUserDecision();
    Logger.info("## BatchManager.resetBatch ### reset batch successfull");
  }

  static async activate({ customId }) {
    Logger.info("## BatchManager.activate ### identifier customId", customId);
    BatchManager.batch.user.getEditor().setIdentifier(customId).save();
    _started = true;

    Logger.info("## BatchManager.activate ### identifier customId saved", customId);
  }

  static async deactivate() {
    // we delete identifier because we dont want to the user receive notif after logout
    // https://doc.batch.com/cordova/custom-data/customid/#choosing-a-unique-identifier
    BatchManager.batch.user.getEditor().setIdentifier(null).save();
    _started = false;

    Logger.info("## BatchManager.deactivate ### identifiers deleted");
  }

  static batchPushReceived({ router }) {
    return (event) => {
      const payload = event.payload;
      Logger.info("## BatchManager.batchPushReceived ### event", event);
      Logger.info("## BatchManager.batchPushReceived ### payload", payload);
    };
  }

  static async registerEvents({ router }) {
    EventManager.on(CORE_EVENTS.ON_LOGOUT, BatchManager.resetBatch);
    document.addEventListener("batchPushReceived", BatchManager.batchPushReceived({ router }), false);
    document.addEventListener("dispatchMessagingEvent", (event) => {
      Logger.info("## dispatchMessagingEvent ### event", event);
    });
  }

  static disableDoNotDisturb() {
    if (!isNative) {
      return;
    }

    BatchManager.batch.messaging.setDoNotDisturbEnabled(false);
    Logger.info("## BatchManager.messaging ### we set setDoNotDisturbEnabled at false");
    BatchManager.batch.messaging.showPendingMessage();
    Logger.info("## BatchManager.messaging ### we show pending message");
  }

  static sendEvent({ name = "", route = {} }) {
    if (!BatchManager.batchStarted()) {
      return;
    }
    let eventData = new BatchManager.batch.user.eventData();

    eventData.put("name", route.name);
    eventData.put("path", route.path);

    BatchManager.batch.user.trackEvent(name, null, eventData);
  }
}
